/**
 * Canonical model list order, matching `get_tone_with_models` / tone pages:
 * `position ASC NULLS LAST`, then `created_at DESC`, then `id ASC`.
 *
 * Every v1 surface that returns models for a tone (list, select, zip) must use
 * this so partners see the same order as the website.
 *
 * The `id ASC` leg is not cosmetic. A capture batch inserts every model with
 * one timestamp, so `(position, created_at)` is a total tie for the whole
 * pack; without a unique final key the row order is unspecified and paging
 * `/api/v1/models` re-runs the tie per page, so rows repeat on one page and
 * vanish from another (measured: 17 of 111 models missing on tone 6298 at
 * page_size=15). `id` is unique and non-null, which makes the sort total.
 */

type Orderable = {
  order: (
    column: string,
    options?: {
      ascending?: boolean;
      nullsFirst?: boolean;
      referencedTable?: string;
    }
  ) => Orderable;
};

/** Apply the tone-page order to a Supabase models query (or an embedded `models` relation). */
export function orderModelsByTonePage<T extends Orderable>(
  query: T,
  opts?: { referencedTable?: string }
): T {
  const extra = opts?.referencedTable ? { referencedTable: opts.referencedTable } : {};
  return query
    .order('position', { ascending: true, nullsFirst: false, ...extra })
    .order('created_at', { ascending: false, ...extra })
    .order('id', { ascending: true, ...extra }) as T;
}

type Positioned = {
  id?: number | null;
  position?: number | null;
  created_at?: string | null;
};

/** Same comparison as `ORDER BY position ASC NULLS LAST, created_at DESC, id ASC`. */
export function compareModelsByTonePageOrder(a: Positioned, b: Positioned): number {
  if (a.position != null || b.position != null) {
    if (a.position == null) return 1;
    if (b.position == null) return -1;
    if (a.position !== b.position) return a.position - b.position;
  }
  const byCreatedAt =
    new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime();
  if (byCreatedAt !== 0) return byCreatedAt;
  return (a.id ?? 0) - (b.id ?? 0);
}
